// -------------------------------------------------------------------------------------------------
// Import
// -------------------------------------------------------------------------------------------------

import {
  forEach as _forEach,
  throttle as _throttle,
  trim as _trim,
} from 'lodash-es';
import $ from 'jquery';
import {getElementOffset, getWindowScrollPos} from '../_util';

// -------------------------------------------------------------------------------------------------
// Variables
// -------------------------------------------------------------------------------------------------

const baseClass = 'content-with-sidebar';
const selectors = {
  main: `.${baseClass}__main`,
  aside: `.${baseClass}__aside`,
  asideOuter: `.${baseClass}__aside-outer`,
  asideInner: `.${baseClass}__aside-inner`,
};

// -------------------------------------------------------------------------------------------------
// Classes
// -------------------------------------------------------------------------------------------------

class ContentWithSidebar {
  constructor(el) {
    this.anchors = [];
    this._activeItem = null;
    this.intersectingElements = new Set();
    this.sidebar = null;

    this.el = el;
    this.$main = $(el).find(selectors.main);
    this.$asideOuter = $(el).find(selectors.asideOuter);
    this.$indexList = $(el).find('.index-list');

    this.initialize();
  }

  get activeItem() {
    return this._activeItem;
  }

  set activeItem(i) {
    if (i !== this._activeItem) {
      this._activeItem = i;
      this.$indexList.find('li').removeClass('active').eq(i).addClass('active');

      const normalItems = document.querySelectorAll('.page-header__content-nav__inner .nav-link');
      for (const item of normalItems) {
        item.classList.remove('active');
      }
      normalItems[i]?.classList.add('active');

      const dropdownItems = document.querySelectorAll('.page-header__content-nav__inner .dropdown-item');
      for (const item of dropdownItems) {
        item.classList.remove('active');
      }
      dropdownItems[i]?.classList.add('active');
    }
  }

  async initialize() {
    this.crawlAnchors();
    this.buildNav();

    const { default: StickySidebar } = await import('sticky-sidebar');

    this.sidebar = new StickySidebar(this.$asideOuter[0], {
      topSpacing: 122,
      containerSelector: selectors.aside,
      innerWrapperSelector: selectors.asideInner,
    });

    $(window).on('scroll resize load', _throttle(this.updateNav.bind(this), (1000 / 8)));

    this.updateNav();
    this.observer();
  }

  crawlAnchors() {
    this.$main.find('[data-anchor-label]').each((i, e) => {
      const id = _trim(e.id);
      const label = _trim(e.dataset.anchorLabel);
      let waypoint = $(e).find('.scroll-anchor')[0];
      if (waypoint == null) {
        waypoint = e;
      }

      if (id && label) {
        this.anchors.push({ id, label, waypoint });
      }
    });
  }

  buildNav() {
    const links = document.querySelector('.leaf__body-links');
    const headline = document.querySelector('.leaf--sidebar h2');
    const element = this.createPetrolNavigation();

    const itemList = element.querySelector('ul');
    _forEach(this.anchors, (anchor) => {
      this.$indexList.append(`<li><a href="#${anchor.id}">${anchor.label}</a></li>`);

      const item = document.createElement('li');
      item.classList.add('nav-item');
      item.innerHTML = `<a class="nav-link" aria-current="page" href="#${anchor.id}">${anchor.label}</a>`;

      itemList.append(item);
    });

    if (document.querySelector('header.page-header .page-header__content-nav-wrapper')) {
      document.querySelector('header.page-header .page-header__content-nav-wrapper').prepend(element);
    }

    let dropdownMenuGroup = document.querySelector('.dropdown-menu__group');

    if (dropdownMenuGroup) {
      if (headline) {
        setTimeout(function (){
          dropdownMenuGroup.prepend(headline.cloneNode(true));
        },250)
      }

      if (links) {
        setTimeout(function (){
          dropdownMenuGroup.innerHTML += `<div class="dropdown-menu__group-links">${links.innerHTML}</div>`;
        },250)
      }
    }
  }

  updateNav() {
    const winScrollPos = getWindowScrollPos();
    let lastPassedItem = 0;
    _forEach(this.anchors, (anchor, i) => {
      const offset = getElementOffset(anchor.waypoint);
      if (Math.ceil(winScrollPos.top) >= Math.floor(offset.top)) {
        lastPassedItem = i;
      }
    });
    this.activeItem = lastPassedItem;
  }

  createPetrolNavigation() {
    const element = document.createElement('nav');
    element.classList.add('page-header__content-nav');

    element.innerHTML = `<div class="page-header__content-nav__inner">
        <ul class="nav nav-pills nav-pills--subpages nav-pills--hide-overflow sidebar-menu-mobile">
        </ul>
    </div>`;

    return element;
  }

  observer() {
    const observerElement = $('.content-section--observer');
    const contentObserver = new IntersectionObserver(elements => {
      for (const element of elements) {
        if (element.isIntersecting) {
          this.intersectingElements.add(element.target);
        } else {
          this.intersectingElements.delete(element.target);
        }
      }

      if (this.intersectingElements.size > 0) {
        $(`.${baseClass}__aside`).addClass('is-hidden');
      } else {
        $(`.${baseClass}__aside`).removeClass('is-hidden');
      }
    }, {rootMargin: "-30%"})

    _forEach(observerElement, (item) => {
      contentObserver.observe(item);
    });
  }
}

// -------------------------------------------------------------------------------------------------
// Methods
// -------------------------------------------------------------------------------------------------

function init() {
  $(`.${baseClass}`).each((i, el) => new ContentWithSidebar(el));
}

// -------------------------------------------------------------------------------------------------
// Export
// -------------------------------------------------------------------------------------------------

export default {
  init,
};
